import { useState } from 'react';

import { FAIconName, TagProps, useToastMessage } from '@equip.health/ui';

import { InquiryEvents, TreatmentEpisodeEvents } from '~/analytics';
import { InquiryStatusTagDropdownOptions } from '~/components/maud/inquiries/InquiryViewsTable/utils';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  useEnrollInquiryMutation,
  useGetInquiryStatusesQuery,
  useUpdateInquiryStatusMutation,
} from '~/store/services/inquiry';
import { TreatmentEpisodeProgramDetails } from '~/components/maud/patients/treatmentEpisode/Types';
import { useTreatmentPrograms } from '~/lib/hooks/useTreatmentPrograms';
import { useAutoRegisterPatientMutation } from '~/store/services/patients';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface UseEditInquiryStatusProps {
  currentStatus?: string;
  showConflictErrorModal?: boolean;
  source: 'Inquiry List' | 'Inquiry Detail';
}

interface CloseInquiryParams {
  closedReasonId: string;
  noShowEmail?: boolean;
  inquiryId: string;
  version: string;
  pathname?: string;
  patientId?: string;
}

interface UpdateStatusParams {
  id: string;
  pathname: string;
  patientId: string;
  version: string;
  status: InquiryEnum;
  closedReasonId?: string;
  noShowEmail?: boolean;
}

export interface UseEditInquiryStatusState {
  cancel: () => void;
  changeStatus: (
    inquiryId: string,
    statusOptionTagId: string,
    version: string,
    pathname?: string,
    patientId?: string,
  ) => Promise<void | VersionedResponse>;
  closeInquiry: ({
    inquiryId,
    closedReasonId,
    version,
    pathname,
    patientId,
  }: CloseInquiryParams) => Promise<void | VersionedResponse>;
  enroll: (
    inquiryId: string,
    admitDate: string,
    version: string,
    treatmentEpisodePrograms?: TreatmentEpisodeProgramDetails[],
    pathname?: string,
    patientId?: string,
    treatmentProgramName?: string,
  ) => Promise<void | VersionedResponse>;
  isSaving: boolean;
  showClosedReasonModal: boolean;
  showCreateTreatmentEpisodeModal: boolean;
  statusOptions: TagProps[];
  treatmentProgramOptions: string[];
}

const useEditInquiryStatus = ({
  source,
  showConflictErrorModal,
  currentStatus,
}: UseEditInquiryStatusProps): UseEditInquiryStatusState => {
  const { newEpisodePrograms: treatmentProgramOptions } =
    useTreatmentPrograms();

  const { groupsAutoRegisterNfoGroups: isGroupsAutoRegisterNfoGroupsEnabled } =
    useFlags();
  const { createToast } = useToastMessage();

  const { data: inquiryStatuses } = useGetInquiryStatusesQuery();
  const [triggerUpdateInquiryStatus, { isLoading: isUpdatingInquiryStatus }] =
    useUpdateInquiryStatusMutation();
  const [triggerEnroll, { isLoading: isEnrolling }] =
    useEnrollInquiryMutation();
  const [triggerAutoRegisterPatient] = useAutoRegisterPatientMutation();

  const { trackEvent } = useAnalytics();

  const [showClosedReasonModal, setShowClosedReasonModal] =
    useState<boolean>(false);
  const [showCreateTreatmentEpisodeModal, setShowCreateTreatmentEpisodeModal] =
    useState<boolean>(false);

  const getStatusNameFromTagId = (tagId: string) =>
    InquiryStatusTagDropdownOptions.find((option) => option.id === tagId);

  const updateStatus = async ({
    id,
    pathname,
    patientId,
    version,
    status,
    noShowEmail,
    closedReasonId,
  }: UpdateStatusParams): Promise<VersionedResponse> => {
    trackEvent(InquiryEvents.statusUpdated, {
      inquiryId: id,
      inquiryStatus: status.name,
      source,
    });

    return triggerUpdateInquiryStatus({
      closedReasonId,
      inquiryId: id,
      noShowEmail,
      pathname,
      patientId,
      showConflictErrorModal,
      status: status.name,
      statusId: status.id,
      version,
    }).unwrap();
  };

  /**
   * Change status of an inquiry.
   * @param inquiryId The inquiry id
   * @param statusOptionTagId Id of status option tag
   * @param version Inquiry version
   * @param pathname Optional current pathname
   * @param patientId Optional patient id
   */
  const changeStatus = async (
    inquiryId: string,
    statusOptionTagId: string,
    version: string,
    pathname?: string,
    patientId?: string,
  ): Promise<void | VersionedResponse> => {
    const newInquiryStatusProp = getStatusNameFromTagId(statusOptionTagId);

    if (!newInquiryStatusProp || newInquiryStatusProp.value === currentStatus) {
      return Promise.resolve();
    }

    const newInquiryStatus: InquiryEnum = inquiryStatuses.find(
      (s) => s.name === newInquiryStatusProp.value,
    );

    if (!newInquiryStatus) {
      return Promise.resolve();
    }

    if (newInquiryStatus.name === InquiryStatusValue.CLOSED) {
      setShowClosedReasonModal(true);
    } else if (newInquiryStatus.name === InquiryStatusValue.ENROLLED) {
      setShowCreateTreatmentEpisodeModal(true);
    } else {
      return updateStatus({
        id: inquiryId,
        pathname,
        patientId,
        status: newInquiryStatus,
        version,
      });
    }
    return Promise.resolve();
  };

  /**
   * Close an inquiry
   * @param inquiryId The inquiry id
   * @param closedReasonId Id of reason it's being closed
   * @param version Inquiry version
   * @param pathname Optional current pathname
   * @param patientId Optional patient id
   */
  const closeInquiry = async ({
    inquiryId,
    closedReasonId,
    version,
    pathname,
    patientId,
    noShowEmail,
  }: CloseInquiryParams) => {
    return updateStatus({
      closedReasonId,
      id: inquiryId,
      noShowEmail,
      pathname,
      patientId,
      status: inquiryStatuses?.find(
        (x) => x.name === InquiryStatusValue.CLOSED,
      ),
      version,
    }).then(() => setShowClosedReasonModal(false));
  };

  /**
   * Change inquiry status to enrolled
   * @param inquiryId Inquiry id
   * @param admitDate Admit date (YYYY-MM-DD)
   * @param version Inquiry version
   * @param treatmentEpisodePrograms Optional array of program details
   * @param pathname Optional current pathname
   * @param patientId Optional patient id
   * @param treatmentProgramName Optional display treatment program name
   */
  const enroll = async (
    inquiryId: string,
    admitDate: string,
    version: string,
    treatmentEpisodePrograms?: TreatmentEpisodeProgramDetails[],
    pathname?: string,
    patientId?: string,
    treatmentProgramName?: string,
  ): Promise<void | VersionedResponse> => {
    trackEvent(InquiryEvents.statusUpdated, {
      inquiryId,
      inquiryStatus: InquiryStatusValue.ENROLLED,
      source,
    });

    return triggerEnroll({
      admitDate,
      inquiryId,
      pathname,
      patientId,
      showConflictErrorModal,
      treatmentEpisodePrograms,
      version,
    })
      .unwrap()
      .then(() => {
        trackEvent(TreatmentEpisodeEvents.createdTreatmentEpisode, {
          inquiry_id: inquiryId,
          source,
          treatment_program:
            treatmentProgramName ||
            treatmentEpisodePrograms?.[0]?.treatmentProgramName,
        });
        if (patientId && admitDate && isGroupsAutoRegisterNfoGroupsEnabled) {
          triggerAutoRegisterPatient({
            patientExternalId: patientId,
            admitDate,
          })
            .unwrap()
            .catch(() => {
              createToast({
                icon: FAIconName.exclamationTriangleRegular,
                id: 'auto-register-patient-failure_toast',
                text: 'Unable to auto-register supports to the New Family Orientation group. Please try again or contact support.',
                variant: 'critical',
              });
            });
        }
        setShowCreateTreatmentEpisodeModal(false);
      });
  };

  const cancel = () => {
    setShowClosedReasonModal(false);
    setShowCreateTreatmentEpisodeModal(false);
  };

  return {
    cancel,
    changeStatus,
    closeInquiry,
    enroll,
    isSaving: isUpdatingInquiryStatus || isEnrolling,
    showClosedReasonModal,
    showCreateTreatmentEpisodeModal,
    // TODO: We should be using data from the backend rather than hard coded value. But the backend
    //  does not sort or provide color.
    statusOptions: InquiryStatusTagDropdownOptions,
    treatmentProgramOptions,
  };
};

export default useEditInquiryStatus;
